import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenantSession } from "@/contexts/TenantSessionContext";
import { apiForgotPassword } from "@/lib/api";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSession();

  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError("");
    try {
      await apiForgotPassword(email.trim());
      setSubmitted(true);
    } catch (err: any) {
      // We always want the user to see the generic success message even on
      // server errors — otherwise the response itself becomes an enumeration
      // vector. Only surface validation problems (bad email format).
      if (err?.status === 400) {
        setError(err.message || t("forgotPassword.error", "Please enter a valid email."));
      } else {
        setSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/40 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl shadow-elevated p-8 sm:p-10">
          <div className="text-center mb-6">
            <img src="/besthr.png" alt="BestHR" className="h-10 mx-auto mb-4" />
            {tenantSlug && tenantSlug !== "public" && (
              <span className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 font-mono font-medium">
                {tenantSlug}
              </span>
            )}
          </div>

          {submitted ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-foreground">
                {t("forgotPassword.successTitle")}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("forgotPassword.successMessage")}
              </p>
              <Button
                variant="outline"
                className="w-full h-11 mt-4"
                onClick={() => navigate("/login")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("forgotPassword.backToLogin")}
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground text-center">
                  {t("forgotPassword.title")}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm text-center">
                  {t("forgotPassword.subtitle")}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    {t("forgotPassword.emailLabel")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("login.emailPlaceholder")}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    required
                    autoComplete="email"
                    autoFocus
                    className="h-11"
                  />
                </div>

                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 gradient-brand text-primary-foreground font-medium"
                  disabled={isLoading || !email.trim()}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("forgotPassword.sending", "Sending…")}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t("forgotPassword.sendButton")}
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft className="h-3 w-3" />
                  {t("forgotPassword.backToLogin")}
                </button>
              </div>
            </>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">
          {t("login.copyright")}
        </p>
      </div>
    </div>
  );
}
